import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridActionsCellItem, gridClasses } from '@mui/x-data-grid';
import { intentionallyFloatingPromiseReturn } from 'core/utils/commonUtils';
import useTags, { SWR_PRODUCT_TAGS_ENDPOINT } from 'hooks/swr/useTags';
import CardedPage from 'layout/pages/CardedPage';
import { isArray, isNil } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as tagsService from 'services/tagService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import TagDialog from './TagDialog';
import TagsPageHeader from './TagsPageHeader';
import type { GridColDef } from '@mui/x-data-grid';
import type { Tag } from 'models/tag';
import type { FC } from 'react';

const TagsPage: FC = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState({
    page: +(searchParams.get('pageIndex') ?? 0),
    pageSize: +(searchParams.get('pageSize') ?? 50),
  });
  const [searchText, setSearchText] = useState(searchParams.get('searchText') ?? '');

  const [editedTag, setEditedTag] = useState<Tag>();
  const [openDialog, setOpenDialog] = useState(false);
  const deletionConfirm = useConfirm();

  const { userStore } = useContext(StoreContext);

  const ability = userStore.ability;

  const { data, isLoading } = useTags({
    pageIndex: 0,
    pageSize: 25,
    filterText: searchText,
  });

  useEffect(() => {
    setSearchParams({
      pageIndex: String(paginationModel.page),
      pageSize: String(paginationModel.pageSize),
      searchText,
    });
  }, [setSearchParams, paginationModel, searchText]);

  const resetTagsData = async (): Promise<unknown> =>
    await mutate(
      (key) => isArray(key) && key[0] === SWR_PRODUCT_TAGS_ENDPOINT,
      undefined
    );

  const handleDeleteCategory = useCallback(
    (id: number) => async () => {
      await deletionConfirm({
        description: 'Удалить тег?',
      })
        .then(async () => {
          await tagsService.deleteTagAsync(id);
          return await resetTagsData();
        })
        .catch(console.log);
    },
    [deletionConfirm]
  );

  const columns: Array<GridColDef<Tag>> = [
    {
      field: 'name',
      headerName: 'Название',
      flex: 1,
      sortable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params) =>
        [
          ability.can('update', 'Tags') && (
            <GridActionsCellItem
              key={`edit-${params.row.id}`}
              icon={<EditIcon />}
              onClick={() => {
                setEditedTag(params.row);
                setOpenDialog(true);
              }}
              label='Редактировать'
            />
          ),
          ability.can('delete', 'Tags') && (
            <GridActionsCellItem
              key={`delete-${params.row.id}`}
              icon={<DeleteOutlineIcon />}
              onClick={() => {
                intentionallyFloatingPromiseReturn(handleDeleteCategory(params.row.id))();
              }}
              label='Удалить'
            />
          ),
        ].filter((action): action is JSX.Element => Boolean(action)),
    },
  ];

  const handleDialogClose = (): void => {
    setOpenDialog(false);
    setEditedTag(undefined);
  };

  const onCreateClickHandle: VoidFunction = () => {
    setEditedTag(undefined);
    setOpenDialog(true);
  };

  return (
    <CardedPage
      disablePadding
      header={
        <TagsPageHeader
          onCreateClick={onCreateClickHandle}
          onSearchChanged={setSearchText}
          searchTextInit={searchText}
        />
      }
      title='Теги'>
      <TagDialog open={openDialog} tag={editedTag} onClose={handleDialogClose} />
      <DataGrid
        sx={{
          [`& .${gridClasses.cell}:not(.${gridClasses.cell}Checkbox):not([data-field="actions"])`]:
            {
              py: 2,
            },
          [`& .${gridClasses.cell}:focus`]: {
            outline: 'none',
          },
        }}
        rows={data?.items ?? []}
        rowCount={data?.totalCount ?? 0}
        paginationModel={paginationModel}
        paginationMode='server'
        onPaginationModelChange={setPaginationModel}
        getRowHeight={() => 'auto'}
        columns={columns}
        loading={isLoading}
        hideFooterPagination={isNil(data) || data.totalPages <= 1}
        hideFooter={isNil(data) || data.totalPages <= 1}
        autoHeight
        disableRowSelectionOnClick
        disableColumnMenu
      />
    </CardedPage>
  );
});

export default TagsPage;
